import { Link } from "react-router-dom";
import { 
  ArrowRight, CheckCircle, Server, Database, Layers, Settings,
  Shield, Clock, Code, GitBranch
} from "lucide-react";
import { Header, Footer, SectionHeader } from "../components/Orchestrio";
import { trackBookCallClick } from "../utils/analytics";
import SEO from "../components/SEO";

const HomePage = () => {
  const handleBookCallClick = (location) => {
    trackBookCallClick(location);
  };

  // Core capabilities - systems-focused
  const capabilities = [
    {
      icon: <Server className="w-5 h-5" />,
      title: "Marketing Websites",
      description: "Fast, accessible, SEO-ready. Built to perform and easy to maintain.",
      link: "/services/websites"
    },
    {
      icon: <Database className="w-5 h-5" />,
      title: "Web Applications",
      description: "Dashboards, portals, and internal tools with proper architecture.",
      link: "/services/web-apps"
    },
    {
      icon: <Layers className="w-5 h-5" />,
      title: "System Integrations",
      description: "Connect your tools. Automate your workflows. Reduce manual work.",
      link: "/services/integrations"
    },
    {
      icon: <Settings className="w-5 h-5" />,
      title: "Ongoing Support",
      description: "Maintenance, updates, and improvements. We're in it for the long term.",
      link: "/services/support"
    }
  ];

  // Proof points - factual, no hype
  const proofPoints = [
    { value: "10+", label: "Years in operation" },
    { value: "150+", label: "Projects delivered" },
    { value: "98%", label: "Delivered on time" },
    { value: "UK", label: "Based & registered" }
  ];

  // What we actually do differently
  const principles = [
    {
      title: "We document everything",
      description: "Every system we build comes with clear documentation. Your next developer won't be guessing."
    },
    {
      title: "We version control properly",
      description: "Git-based workflows, staged deployments, rollback capability. Standard practice, not optional extras."
    },
    {
      title: "We don't disappear after launch",
      description: "Ongoing support agreements available. We maintain what we build."
    },
    {
      title: "We give you access to everything",
      description: "Your code, your hosting, your domains. No lock-in. Ever."
    }
  ];

  // Selected work - outcomes focused
  const selectedWork = [
    {
      client: "Legal Partners LLP",
      type: "Client Portal",
      outcome: "70% reduction in support queries",
      description: "Self-service document portal replacing email-based workflows"
    },
    {
      client: "Northern Supplies",
      type: "Order System",
      outcome: "4 hours → 10 minutes processing",
      description: "Automated order pipeline with supplier integrations"
    },
    {
      client: "Bloom Health",
      type: "Booking Platform",
      outcome: "3x booking conversion rate",
      description: "Rebuilt booking system with proper UX and faster checkout"
    }
  ];

  // Testimonials - specific, credible
  const testimonials = [
    {
      quote: "They actually read our requirements. The system they built does exactly what we asked for—no more, no less. It just works.",
      name: "James Crawford",
      role: "Operations Director",
      company: "Crawford & Sons Logistics"
    },
    {
      quote: "Our previous agency left us with a mess. Orchestrio rebuilt it properly and documented everything. We can actually maintain it now.",
      name: "Sarah Mitchell",
      role: "Technical Lead",
      company: "GreenTech Solutions"
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="home-page">
      <SEO />
      <Header />

      {/* ============ HERO ============ */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-6">
          {/* Minimal lead-in */}
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            UK-based digital systems studio
          </p>

          {/* Main headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.15] mb-6 max-w-4xl">
            Websites and applications that actually work.
          </h1>
          
          {/* Supporting copy - direct, no fluff */}
          <p className="text-lg md:text-xl text-[#9CA3AF] mb-10 max-w-2xl leading-relaxed">
            We design and build digital systems with performance, maintainability, 
            and clarity as the priorities. Not trends. Not gimmicks.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="hero-contact"
            >
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/work" 
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="hero-view-work"
            >
              View our work
            </Link>
          </div>

          {/* Proof strip - facts only */}
          <div className="flex flex-wrap gap-x-8 gap-y-4 text-sm">
            {proofPoints.map((point, i) => (
              <div key={i} className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-[#F4F6F8]">{point.value}</span>
                <span className="text-[#6B7280]">{point.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHAT WE BUILD ============ */}
      <section className="py-20 border-t border-[#2E2E2E]" id="services">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">What we build</h2>
            <p className="text-[#9CA3AF] max-w-xl">
              Websites, web applications, and integrations. Built as systems, not one-off projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.map((item, index) => (
              <Link 
                key={index}
                to={item.link}
                className="group p-6 bg-[#242424] border border-[#2E2E2E] hover:border-[#3A3A3A] transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#2F5D8C]/15 flex items-center justify-center text-[#5F8FB8] rounded flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 group-hover:text-[#5F8FB8] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#9CA3AF] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline inline-flex items-center gap-1">
              All services <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ HOW WE'RE DIFFERENT ============ */}
      <section className="py-20 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">How we work differently</h2>
            <p className="text-[#9CA3AF] max-w-xl">
              If you've worked with agencies before, you know what usually goes wrong. 
              We've structured our process to prevent those problems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-[#2F5D8C]/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-3.5 h-3.5 text-[#5F8FB8]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{principle.title}</h3>
                  <p className="text-sm text-[#9CA3AF] leading-relaxed">{principle.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-[#2E2E2E]">
            <Link to="/process" className="text-[#5F8FB8] text-sm hover:underline inline-flex items-center gap-1">
              See our full process <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ SELECTED WORK ============ */}
      <section className="py-20 border-t border-[#2E2E2E]" id="work">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Selected work</h2>
            <p className="text-[#9CA3AF] max-w-xl">
              Systems we've built. Outcomes we've delivered.
            </p>
          </div>

          <div className="space-y-6">
            {selectedWork.map((work, index) => (
              <div 
                key={index}
                className="p-6 bg-[#242424] border border-[#2E2E2E]"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#5F8FB8] text-sm font-medium">{work.type}</span>
                      <span className="text-[#3A3A3A]">·</span>
                      <span className="text-[#6B7280] text-sm">{work.client}</span>
                    </div>
                    <p className="text-[#9CA3AF] text-sm">{work.description}</p>
                  </div>
                  <div className="md:text-right flex-shrink-0">
                    <div className="text-lg font-semibold text-[#F4F6F8]">{work.outcome}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/work" className="btn-secondary inline-flex items-center gap-2">
              View all work <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-20 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">What clients say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
                <blockquote className="text-[#9CA3AF] mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <div className="font-semibold text-sm">{testimonial.name}</div>
                  <div className="text-[#6B7280] text-sm">{testimonial.role}, {testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING INDICATOR ============ */}
      <section className="py-20 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Clear, honest pricing</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Most projects fall between £5,000 and £50,000. We'll give you a proper estimate 
                after understanding your requirements. No surprises.
              </p>
            </div>
            <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2 flex-shrink-0">
              View pricing guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to build properly?
          </h2>
          <p className="text-[#9CA3AF] mb-8 max-w-lg mx-auto">
            Get in touch to discuss your project. No sales pitch—just a conversation 
            about what you need and whether we're the right fit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              onClick={() => handleBookCallClick('footer-cta')}
            >
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
              Send a message
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
export { Header, Footer } from "../components/Orchestrio";
